import React, { useState, useEffect } from 'react';
import {
    Card, Switch, Button, Input, Form, message, Spin, Typography, theme, Space, Divider
} from 'antd';
import {
    BellOutlined, MailOutlined, CheckOutlined, CloseOutlined, SaveOutlined
} from '@ant-design/icons';
import { getSystemSettings, updateSystemSettings } from '../../../services/settings/systemSettingsApi';

const { Text, Title } = Typography;

const SystemSettings = () => {
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sendNotification, setSendNotification] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await getSystemSettings();
            const s = res.data.setting;
            setSendNotification(!!s.send_notification);
            form.setFieldsValue({
                system_mail: s.system_mail || '',
            });
        } catch (err) {
            message.error('Failed to load system settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }
        setSaving(true);
        try {
            await updateSystemSettings({
                send_notification: sendNotification,
                system_mail: values.system_mail || null,
            });
            message.success('System settings saved successfully');
        } catch (err) {
            message.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <BellOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
                    <span>System Settings</span>
                </div>
            }
            style={{ margin: '16px' }}
            extra={
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            }
        >
            {/* Notification Toggle Row */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 0',
                }}
            >
                <Space align="start" size={14}>
                    <MailOutlined style={{ fontSize: 24, color: token.colorPrimary, marginTop: 2 }} />
                    <div>
                        <Title level={5} style={{ margin: 0 }}>Email Notifications</Title>
                        <Text type="secondary">
                            Send an email alert to the system mail address every time a client sends a support message.
                        </Text>
                    </div>
                </Space>
                <Switch
                    checked={sendNotification}
                    onChange={setSendNotification}
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                />
            </div>

            <Divider style={{ margin: '8px 0 24px' }} />

            {/* Email Input */}
            <Form form={form} layout="vertical">
                <Form.Item
                    name="system_mail"
                    label={
                        <Text strong>
                            <MailOutlined style={{ marginRight: 6 }} />
                            Notification Email Address
                        </Text>
                    }
                    rules={[
                        { type: 'email', message: 'Please enter a valid email address' },
                        ...(sendNotification
                            ? [{ required: true, message: 'Email is required when notifications are enabled' }]
                            : []),
                    ]}
                    extra="Alert emails will be sent to this address when a client messages support."
                >
                    <Input
                        placeholder="contact@example.com"
                        prefix={<MailOutlined style={{ color: token.colorTextDescription }} />}
                        size="large"
                    />
                </Form.Item>
            </Form>

            {/* Info box */}
            <div
                style={{
                    marginTop: 8,
                    padding: '14px 18px',
                    backgroundColor: token.colorFillAlter,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                }}
            >
                <Text type="secondary">
                    <strong>Note:</strong> When enabled, an HTML-formatted email containing the client's name,
                    email address, and message will be sent to the address above each time a support message
                    is received.
                </Text>
            </div>

            {/* Status summary */}
            <div style={{ marginTop: 16 }}>
                <Text strong>Current Status: </Text>
                {sendNotification ? (
                    <span style={{
                        display: 'inline-block',
                        background: '#f6ffed',
                        color: '#52c41a',
                        border: '1px solid #b7eb8f',
                        borderRadius: 12,
                        padding: '2px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                    }}>
                        🔔 Notifications Enabled
                    </span>
                ) : (
                    <span style={{
                        display: 'inline-block',
                        background: '#f5f5f5',
                        color: '#8c8c8c',
                        border: '1px solid #d9d9d9',
                        borderRadius: 12,
                        padding: '2px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                    }}>
                        🔕 Notifications Disabled
                    </span>
                )}
            </div>
        </Card>
    );
};

export default SystemSettings;
